/**
 * useHospitalLocais - Hook for local de contagem CRUD operations
 *
 * Manages:
 * - Local form state
 * - Create/Update/Delete operations
 * - Editing mode
 */
import { useState } from 'react';
import apiClient from '../services/apiClient';
import { msg, MESSAGES } from '../utils/messages';
import type { LocalContagem, Hospital } from './useHospitalData';
import { validateEmail, formatTelefone } from './useHospitalForm';

// NOTE: apiClient already has baseURL with /api, so we use relative paths

interface NewLocalState {
  local: string;
  responsavel: string;
  telefone: string;
  ramal: string;
  email: string;
}

// Story 2.34: contato extra (N contatos por local)
// id = undefined quando contato e novo (ainda nao foi POSTado)
// _toDelete = true quando contato foi marcado pra remocao no save
export interface ContatoExtraDraft {
  id?: number;
  nome: string;
  telefone: string;
  email: string;
  ramal: string;
  cargo: string;
  ordem: number;
  ativo: boolean;
  _toDelete?: boolean;
  _dirty?: boolean;  // marcado true quando o campo foi editado (para detectar diffs)
}

const initialLocalState: NewLocalState = {
  local: '',
  responsavel: '',
  telefone: '',
  ramal: '',
  email: ''
};

interface UseHospitalLocaisProps {
  selectedHospital: Hospital | null;
  token: string | null;
  refreshLocais: () => Promise<void>;
  setShowLocalModal: (show: boolean) => void;
}

export function useHospitalLocais({
  selectedHospital,
  token,
  refreshLocais,
  setShowLocalModal,
}: UseHospitalLocaisProps) {
  // Form state
  const [newLocal, setNewLocal] = useState<NewLocalState>(initialLocalState);
  const [savingLocal, setSavingLocal] = useState(false);
  const [editingLocal, setEditingLocal] = useState<LocalContagem | null>(null);
  // Story 2.34: contatos extras (alem do principal inline)
  const [contatosExtras, setContatosExtras] = useState<ContatoExtraDraft[]>([]);

  // Update form field
  const updateLocalField = (field: keyof NewLocalState, value: string) => {
    if (field === 'telefone') {
      setNewLocal(prev => ({ ...prev, [field]: formatTelefone(value) }));
    } else if (field === 'ramal') {
      setNewLocal(prev => ({ ...prev, [field]: value.replace(/\D/g, '').slice(0, 6) }));
    } else {
      setNewLocal(prev => ({ ...prev, [field]: value }));
    }
  };

  // Reset form
  const resetForm = () => {
    setNewLocal(initialLocalState);
    setEditingLocal(null);
    setContatosExtras([]);  // Story 2.34
  };

  // Close modal and reset form
  const handleCloseLocalModal = () => {
    resetForm();
    setShowLocalModal(false);
  };

  // Open edit modal
  const handleEditLocal = async (local: LocalContagem) => {
    setEditingLocal(local);
    setNewLocal({
      local: local.local,
      responsavel: local.responsavel,
      telefone: local.telefone || '',
      ramal: local.ramal || '',
      email: local.email || ''
    });
    // Story 2.34: carrega contatos extras existentes do backend
    if (selectedHospital) {
      try {
        const res = await apiClient.get(
          `/hospitais/${selectedHospital.id}/locais/${local.id}/contatos-extras`
        );
        const extras: ContatoExtraDraft[] = (res.data || []).map((c: any) => ({
          id: c.id,
          nome: c.nome,
          telefone: c.telefone || '',
          email: c.email || '',
          ramal: c.ramal || '',
          cargo: c.cargo || '',
          ordem: c.ordem || 0,
          ativo: c.ativo !== false,
        }));
        setContatosExtras(extras);
      } catch (err) {
        console.error('Erro ao carregar contatos extras:', err);
        setContatosExtras([]);
      }
    } else {
      setContatosExtras([]);
    }
    setShowLocalModal(true);
  };

  // Story 2.34: adicionar nova linha de contato extra (em branco)
  const handleAddContatoExtra = () => {
    setContatosExtras(prev => [...prev, {
      nome: '',
      telefone: '',
      email: '',
      ramal: '',
      cargo: '',
      ordem: prev.length,
      ativo: true,
      _dirty: true,
    }]);
  };

  // Story 2.34: editar campo de um contato extra (por index)
  const handleUpdateContatoExtraField = (
    index: number,
    field: keyof ContatoExtraDraft,
    value: string | number | boolean,
  ) => {
    setContatosExtras(prev => prev.map((c, i) => {
      if (i !== index) return c;
      if (field === 'telefone' && typeof value === 'string') {
        return { ...c, telefone: formatTelefone(value), _dirty: true };
      }
      return { ...c, [field]: value, _dirty: true };
    }));
  };

  // Story 2.34: remover contato extra (marca pra delete se ja tem id, senao remove direto)
  const handleRemoveContatoExtra = (index: number) => {
    setContatosExtras(prev => {
      const item = prev[index];
      if (item.id) {
        // Marca pra delete no save
        return prev.map((c, i) => (i === index ? { ...c, _toDelete: true } : c));
      }
      // Sem id, remove do array
      return prev.filter((_, i) => i !== index);
    });
  };

  // Save local (create or update)
  const handleSaveLocal = async () => {
    // Validate required fields
    if (!newLocal.local.trim() || !newLocal.responsavel.trim()) {
      msg.warning(MESSAGES.HOSPITAL.LOCAL_REQUIRED);
      return;
    }

    // Validate email if provided
    if (newLocal.email.trim() && !validateEmail(newLocal.email.trim())) {
      msg.warning(MESSAGES.VALIDATION.INVALID_EMAIL);
      return;
    }

    if (!selectedHospital) {
      msg.warning(MESSAGES.HOSPITAL.NO_SELECTION);
      return;
    }

    try {
      setSavingLocal(true);

      const payload = {
        local: newLocal.local.trim(),
        responsavel: newLocal.responsavel.trim(),
        telefone: newLocal.telefone.replace(/\D/g, '') || null,
        ramal: newLocal.ramal.trim() || null,
        email: newLocal.email.trim() || null,
        ativo: true
      };

      // Story 2.34: salva o LOCAL primeiro, depois persiste contatos extras
      let savedLocalId: number;
      if (editingLocal) {
        // PUT - Update existing
        await apiClient.put(
          `/hospitais/${selectedHospital.id}/locais/${editingLocal.id}`,
          payload
        );
        savedLocalId = editingLocal.id;
      } else {
        // POST - Create new
        const createRes = await apiClient.post(
          `/hospitais/${selectedHospital.id}/locais`,
          payload
        );
        savedLocalId = createRes.data?.id;
      }

      // Story 2.34: persistir contatos extras.
      // IMPORTANTE: antes, qualquer falha no POST/PUT era engolida com apenas
      // console.error — o local salvava mas o contato sumia SEM feedback
      // (sintoma "só salva o 1º"). Agora acumulamos as falhas e avisamos o
      // usuário, mantendo o modal aberto para retry quando houver erro.
      const extraErrors: string[] = [];
      if (savedLocalId) {
        for (const contato of contatosExtras) {
          const contatoPayload = {
            nome: contato.nome.trim(),
            telefone: contato.telefone.replace(/\D/g, '') || null,
            email: contato.email.trim() || null,
            ramal: contato.ramal.trim() || null,
            cargo: contato.cargo.trim() || null,
            ordem: contato.ordem,
            ativo: contato.ativo,
          };

          try {
            if (contato._toDelete && contato.id) {
              // DELETE existente
              await apiClient.delete(
                `/hospitais/${selectedHospital.id}/locais/${savedLocalId}/contatos-extras/${contato.id}`
              );
            } else if (contato.id && contato._dirty) {
              // PUT existente alterado
              await apiClient.put(
                `/hospitais/${selectedHospital.id}/locais/${savedLocalId}/contatos-extras/${contato.id}`,
                contatoPayload
              );
            } else if (!contato.id && !contato._toDelete && contato.nome.trim()) {
              // POST novo (ignora vazios sem nome)
              await apiClient.post(
                `/hospitais/${selectedHospital.id}/locais/${savedLocalId}/contatos-extras`,
                contatoPayload
              );
            }
          } catch (extraErr: any) {
            console.error('Erro ao persistir contato extra:', extraErr);
            const detail =
              extraErr?.response?.data?.detail ||
              extraErr?.response?.data?.message ||
              extraErr?.message ||
              'erro desconhecido';
            extraErrors.push(`${contato.nome?.trim() || '(sem nome)'}: ${detail}`);
          }
        }
      }

      // Refresh list (mostra os que salvaram com sucesso)
      await refreshLocais();

      // Se houve falha em algum contato extra, avisa e mantém o modal aberto
      // para o usuário rever/retentar — não fecha silenciosamente.
      if (extraErrors.length > 0) {
        msg.error(
          `Não foi possível salvar ${extraErrors.length} contato(s) extra(s): ${extraErrors.join('; ')}`
        );
        return;
      }

      handleCloseLocalModal();

    } catch (err: any) {
      console.error('Erro ao salvar local de contagem:', err);
      // T13: o backend (Django Ninja) devolve a mensagem clara em `detail`
      // (ex.: "Já existe um local ... neste hospital"); antes só líamos `message`
      // e caía sempre no texto genérico.
      msg.error(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        MESSAGES.HOSPITAL.LOCAL_SAVE_ERROR
      );
    } finally {
      setSavingLocal(false);
    }
  };

  // Delete local
  const handleDeleteLocal = async (localId: number, confirm: (opts: any) => Promise<boolean>) => {
    if (!selectedHospital) return;

    const confirmed = await confirm({
      title: 'Deletar Local de Contagem',
      message: MESSAGES.HOSPITAL.LOCAL_CONFIRM_DELETE,
      variant: 'danger',
      confirmText: 'Deletar',
    });

    if (!confirmed) return;

    try {
      await apiClient.delete(
        `/hospitais/${selectedHospital.id}/locais/${localId}`
      );

      await refreshLocais();
      msg.success(MESSAGES.HOSPITAL.LOCAL_DELETED);
    } catch (err) {
      console.error('Erro ao deletar local:', err);
      msg.error(MESSAGES.HOSPITAL.LOCAL_DELETE_ERROR);
    }
  };

  return {
    // Form state
    newLocal,
    setNewLocal,
    updateLocalField,
    savingLocal,
    editingLocal,

    // Story 2.34: contatos extras
    contatosExtras,
    handleAddContatoExtra,
    handleUpdateContatoExtraField,
    handleRemoveContatoExtra,

    // Actions
    handleEditLocal,
    handleSaveLocal,
    handleDeleteLocal,
    handleCloseLocalModal,
    resetForm,
  };
}
